import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { addDays, listOptions } from '@actual-app/core/shared/mtxbudget';
import type { OptimiseOption } from '@actual-app/core/shared/mtxbudget';
import {
  cheapestInTime,
  coversInTime,
  leastShortfall,
  optionsInTime,
  partialOptions,
  shortCharges,
  withOption,
} from '@actual-app/core/shared/mtxbudget-parcours';

import { useNavigate } from '#hooks/useNavigate';

import { askAi } from './ai';
import { useByContent } from './byContent';
import { NotConfigured } from './QuinzainePage';
import {
  hoursLabel,
  Line,
  MtxPage,
  Section,
  shortDate,
  textDate,
  useMoney,
} from './ui';
import { useMtxbudget } from './useMtxbudget';
import { useWalkInputs } from './useParcours';

export function OptimiserPage() {
  const { t } = useTranslation();
  const money = useMoney();
  const navigate = useNavigate();
  const { config, saveConfig, open, debts, reserves, today, isLoading } =
    useMtxbudget();
  const { inputs, occurrences } = useWalkInputs();
  const [proposal, setProposal] = useState<{
    ids: string[];
    explanation: string;
  } | null>(null);

  // The walk, day by day: money that comes after a charge never pays it.
  const shortfall = inputs ? leastShortfall(inputs) : 0;
  const planned = open && config ? config.pays[open.payday]?.hours : undefined;
  const currentHours = planned
    ? (planned[0] + planned[1]) / 2
    : (config?.pay.minHoursPerWeek ?? 0);

  // The same list as long as its content holds: the AI engine is asked once
  // per shortfall, not on every new reference to the same figures.
  const options = useByContent(
    config && inputs && shortfall > 0
      ? optionsInTime(
          inputs,
          listOptions({
            config: config.pay,
            shortfall,
            debts,
            reserves,
            currentHoursPerWeek: currentHours,
          }).concat(partialOptions(inputs, config.partialOk)),
        )
      : [],
  );

  useEffect(() => {
    let cancelled = false;
    if (options.length === 0) {
      return;
    }
    // The engine only sees neutral labels: no creditor, no account.
    const labels = options.map((_, index) => `option-${index + 1}`);
    void askAi<{ chosen: string[]; explanation: string }>(
      'optimise',
      {
        shortfall,
        options: options.map((option, index) => ({
          id: labels[index],
          kind: option.kind,
          frees: option.frees,
          cost: option.cost,
        })),
      },
      ['chosen'],
    ).then(answer => {
      if (cancelled || !answer || !Array.isArray(answer.chosen)) {
        return;
      }
      const ids = answer.chosen.map(
        label => options[labels.indexOf(label)]?.id,
      );
      // A proposal that leaves a charge unpaid on its date is never shown.
      if (
        inputs &&
        ids.every((id): id is string => id !== undefined) &&
        new Set(ids).size === ids.length &&
        coversInTime(
          inputs,
          options.filter(o => ids.includes(o.id)),
        )
      ) {
        setProposal({ ids, explanation: answer.explanation ?? '' });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [inputs, options, shortfall]);

  if (!config) {
    return (
      <MtxPage title={t('Optimiser')} back="/quinzaine">
        {!isLoading && <NotConfigured />}
      </MtxPage>
    );
  }

  function label(option: OptimiseOption): string {
    switch (option.kind) {
      case 'extra-hours':
        return t('{{hours}} de plus par semaine', {
          hours: hoursLabel(option.hoursPerWeek ?? 0),
        });
      case 'minimum':
        return t('Ramener « {{subject}} » au paiement minimum', {
          subject: option.subject,
        });
      case 'defer':
        return t('Reporter « {{subject}} » de {{days}} jours', {
          subject: option.subject,
          days: option.days,
        });
      case 'partial':
        return t('Payer « {{subject}} » en partie le {{date}}', {
          subject: option.subject,
          date: textDate(billDate(option)),
        });
      default:
        return t('Utiliser « {{subject}} »', { subject: option.subject });
    }
  }

  function billDate(option: OptimiseOption): string {
    return occurrences.find(o => o.id === option.ref)?.date ?? today;
  }

  // The charges behind the shortfall, and when the first one falls due.
  const shorts = inputs ? shortCharges(inputs) : [];
  const firstShort = shorts[0];
  const lastShort = shorts[shorts.length - 1];
  const workedShort = shorts.find(
    c => c.status === 'worked' || c.status === 'received',
  );
  const savingsIds = new Set(
    reserves
      .filter(r => r.accountId && !config.cashAccountIds.includes(r.accountId))
      .map(r => r.id),
  );

  /** What an option does, in a line under its name. */
  function detailOf(option: OptimiseOption): string {
    switch (option.kind) {
      case 'reserve': {
        const reserve = reserves.find(r => r.id === option.ref);
        return savingsIds.has(option.ref ?? '')
          ? t('Virement vers le compte courant avant le {{date}}', {
              date: textDate(firstShort?.date ?? today),
            })
          : t('les {{amount}} qui restent', {
              amount: money(reserve?.available ?? option.frees),
            });
      }
      case 'extra-hours': {
        const planned = inputs?.pays?.[option.payday ?? '']?.hours;
        const base = planned
          ? (planned[0] + planned[1]) / 2
          : (config?.pay.minHoursPerWeek ?? 0);
        return t('pour la paie du {{date}} : {{hours}} par semaine', {
          date: textDate(option.payday ?? today),
          hours: hoursLabel(base + (option.hoursPerWeek ?? 0)),
        });
      }
      case 'partial':
        return t('{{now}} ce jour-là, le reste ({{rest}}) le {{date}}', {
          now: money(option.now ?? 0),
          rest: money(option.frees),
          date: textDate(option.payday ?? today),
        });
      case 'defer':
        return t('les paiements des {{days}} prochains jours', {
          days: option.days,
        });
      default:
        return t('les paiements au-delà du minimum');
    }
  }

  // A savings transfer retained lately, until the bank shows it done.
  const lastDecision = config.decisions[config.decisions.length - 1];
  const transfers =
    lastDecision && lastDecision.date >= addDays(today, -14)
      ? reserves.filter(
          r =>
            savingsIds.has(r.id) &&
            r.available > 0 &&
            lastDecision.optionIds.includes(`reserve:${r.id}`),
        )
      : [];

  // Bills paid in part, as asked of their creditor: in the plan until refused.
  const arranged = Object.entries(config.arrangements).map(([id, deal]) => {
    const bill = occurrences.find(o => o.id === `${id}/reste`);
    return {
      id,
      deal,
      name: bill?.name.replace(/ \(reste\)$/, '') ?? id,
      rest: bill ? -bill.amount : 0,
    };
  });
  function setArrangement(id: string, accepted: boolean | null) {
    if (!config) {
      return;
    }
    const arrangements = { ...config.arrangements };
    if (accepted === null) {
      delete arrangements[id];
    } else {
      arrangements[id] = { ...arrangements[id], accepted };
    }
    saveConfig({ ...config, arrangements });
  }

  const fallback = inputs ? cheapestInTime(inputs, options) : [];
  const chosen = proposal
    ? options.filter(o => proposal.ids.includes(o.id))
    : fallback;
  const others = options.filter(o => !chosen.includes(o));
  const chosenCost = chosen.reduce((sum, o) => sum + o.cost, 0);

  function accept() {
    if (!config) {
      return;
    }
    const extra = chosen.find(o => o.kind === 'extra-hours');
    // A bill paid in part is a démarche: in the plan until the creditor says.
    const arrangements = { ...config.arrangements };
    for (const option of chosen) {
      if (option.kind === 'partial' && option.ref && option.payday) {
        arrangements[option.ref] = {
          now: option.now ?? 0,
          restOn: option.payday,
        };
      }
    }
    // The hours the walk was checked with, on the pay they reach.
    const pays =
      extra && inputs
        ? (withOption(inputs, extra).pays ?? config.pays)
        : config.pays;
    saveConfig({
      ...config,
      pays,
      arrangements,
      decisions: [
        ...config.decisions,
        { date: today, shortfall, optionIds: chosen.map(o => o.id) },
      ],
    });
    void navigate('/quinzaine');
  }

  return (
    <MtxPage title={t('Optimiser')} back="/quinzaine">
      <View style={{ gap: 12, paddingBottom: 20 }}>
        {shorts.length > 0 && (
          <Section style={{ padding: '12px 16px', gap: 8 }}>
            <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
              <Trans>Ce qui manque, charge par charge</Trans>
            </Text>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                columnGap: 8,
              }}
            >
              <Text
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  color: theme.errorText,
                }}
              >
                {money(shortfall)}
              </Text>
              <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                {t("d'ici le {{date}}", { date: textDate(lastShort.date) })}
              </Text>
            </View>
            <View style={{ borderTop: `1px solid ${theme.tableBorder}` }}>
              {shorts.map((c, index) => (
                <View
                  key={c.id}
                  style={{
                    flexDirection: 'row',
                    gap: 12,
                    padding: '8px 0',
                    borderBottom:
                      index === shorts.length - 1
                        ? undefined
                        : `1px solid ${theme.tableBorder}`,
                  }}
                >
                  <Text
                    style={{
                      width: 52,
                      flexShrink: 0,
                      fontSize: 12,
                      color: theme.pageTextSubdued,
                    }}
                  >
                    {shortDate(c.date)}
                  </Text>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text>{c.name}</Text>
                    <Text
                      style={{ fontSize: 12, color: theme.pageTextSubdued }}
                    >
                      {c.refusedBy
                        ? t(
                            'refusé par {{card}} : plus de place sur la carte',
                            {
                              card:
                                debts.find(d => d.id === c.refusedBy)?.name ??
                                t('la carte'),
                            },
                          )
                        : c.missing < c.amount
                          ? t(
                              '{{missing}} manquent sur {{amount}} · paie du {{date}}',
                              {
                                missing: money(c.missing),
                                amount: money(c.amount),
                                date: textDate(c.payday),
                              },
                            )
                          : t('rien pour la payer · paie du {{date}}', {
                              date: textDate(c.payday),
                            })}
                    </Text>
                  </View>
                  <Text style={{ whiteSpace: 'nowrap' }}>
                    {money(c.missing)}
                  </Text>
                </View>
              ))}
            </View>
            {workedShort && (
              <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                {t(
                  "La paie du {{date}} est déjà travaillée : des heures en plus n'arriveraient que le {{next}}, trop tard pour ces charges.",
                  {
                    date: textDate(workedShort.payday),
                    next: textDate(addDays(workedShort.payday, 14)),
                  },
                )}
              </Text>
            )}
          </Section>
        )}

        {transfers.length > 0 && (
          <Section title={t('Démarches à faire')}>
            {transfers.map(r => (
              <View
                key={r.id}
                style={{
                  gap: 2,
                  padding: '14px 16px',
                  borderBottom: `1px solid ${theme.tableBorder}`,
                }}
              >
                <Text style={{ fontWeight: 600 }}>
                  {t('Virer « {{name}} » vers le compte courant', {
                    name: r.name,
                  })}
                </Text>
                <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                  {t('{{amount}} avant le {{date}}', {
                    amount: money(r.available),
                    date: textDate(firstShort?.date ?? today),
                  })}
                </Text>
                <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                  {t(
                    'Une fois le virement lu par la banque, il disparaît de cette liste.',
                  )}
                </Text>
              </View>
            ))}
          </Section>
        )}
        {arranged.some(a => !a.deal.accepted) && (
          <Section title={t('Démarches à faire')}>
            {arranged
              .filter(a => !a.deal.accepted)
              .map(a => (
                <View
                  key={a.id}
                  style={{
                    gap: 10,
                    padding: '14px 16px',
                    borderBottom: `1px solid ${theme.tableBorder}`,
                  }}
                >
                  <View style={{ gap: 2 }}>
                    <Text style={{ fontWeight: 600 }}>
                      {t('Payer « {{name}} » en partie', { name: a.name })}
                    </Text>
                    <Text
                      style={{ fontSize: 12, color: theme.pageTextSubdued }}
                    >
                      {t(
                        '{{now}} le jour même, le reste ({{rest}}) le {{date}}',
                        {
                          now: money(a.deal.now),
                          rest: money(a.rest),
                          date: textDate(a.deal.restOn),
                        },
                      )}
                    </Text>
                    <Text
                      style={{ fontSize: 12, color: theme.pageTextSubdued }}
                    >
                      {t(
                        "Le plan compte déjà cet arrangement. S'il est refusé, le calcul revient au paiement entier.",
                      )}
                    </Text>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    <Button
                      variant="primary"
                      onPress={() => setArrangement(a.id, true)}
                      style={{ flex: 1, minHeight: 44, fontWeight: 600 }}
                    >
                      <Trans>C'est accepté</Trans>
                    </Button>
                    <Button
                      onPress={() => setArrangement(a.id, null)}
                      style={{
                        flex: 1,
                        minHeight: 44,
                        fontWeight: 600,
                        color: theme.errorText,
                      }}
                    >
                      <Trans>Refusé</Trans>
                    </Button>
                  </View>
                </View>
              ))}
          </Section>
        )}

        {arranged.some(a => a.deal.accepted) && (
          <Section title={t('Déjà accepté')}>
            {arranged
              .filter(a => a.deal.accepted)
              .map((a, index, all) => (
                <View
                  key={a.id}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderBottom:
                      index === all.length - 1
                        ? undefined
                        : `1px solid ${theme.tableBorder}`,
                  }}
                >
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontWeight: 500 }}>{a.name}</Text>
                    <Text
                      style={{ fontSize: 12, color: theme.pageTextSubdued }}
                    >
                      {t('{{now}} le jour même, {{rest}} le {{date}}', {
                        now: money(a.deal.now),
                        rest: money(a.rest),
                        date: textDate(a.deal.restOn),
                      })}
                    </Text>
                  </View>
                  <Button
                    onPress={() => setArrangement(a.id, null)}
                    style={{ minHeight: 44, color: theme.errorText }}
                  >
                    <Trans>Annuler</Trans>
                  </Button>
                </View>
              ))}
          </Section>
        )}

        {shortfall === 0 && (
          <Section style={{ padding: '12px 16px', gap: 4 }}>
            {arranged.some(a => !a.deal.accepted) && (
              <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                <Trans>Avec la solution retenue</Trans>
              </Text>
            )}
            <Text
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: theme.noticeTextLight,
              }}
            >
              <Trans>Toutes vos échéances sont couvertes</Trans>
            </Text>
            {arranged.some(a => !a.deal.accepted) && (
              <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                <Trans>tant que les démarches ci-dessous sont faites</Trans>
              </Text>
            )}
          </Section>
        )}

        {shortfall > 0 && chosen.length === 0 && (
          <Section style={{ padding: 16 }}>
            <Text style={{ color: theme.errorText }}>
              {t(
                'Aucune combinaison connue ne comble le manque de {{amount}}.',
                { amount: money(shortfall) },
              )}
            </Text>
          </Section>
        )}

        {chosen.length > 0 && (
          <Section
            style={{
              padding: '14px 16px',
              gap: 8,
              border: `2px solid ${theme.buttonPrimaryBackground}`,
            }}
          >
            <View
              style={{ flexDirection: 'row', justifyContent: 'space-between' }}
            >
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {t('Solution proposée')}
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.noticeTextLight,
                }}
              >
                {t('Vérifiée jour par jour')}
              </Text>
            </View>
            {chosen.map(option => (
              <View
                key={option.id}
                style={{
                  flexDirection: 'row',
                  gap: 12,
                  padding: '10px 0',
                  borderTop: `1px solid ${theme.tableBorder}`,
                }}
              >
                <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
                  <Text style={{ fontWeight: 600 }}>{label(option)}</Text>
                  <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                    {detailOf(option)}
                  </Text>
                  {option.kind === 'partial' && (
                    <Text
                      style={{
                        alignSelf: 'flex-start',
                        fontSize: 11,
                        fontWeight: 600,
                        color: theme.warningText,
                        border: `1px solid ${theme.warningBorder}`,
                        borderRadius: 4,
                        padding: '1px 6px',
                      }}
                    >
                      <Trans>Démarche à faire</Trans>
                    </Text>
                  )}
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  <Text style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {money(option.frees)}
                  </Text>
                  <Text
                    style={{
                      fontSize: 12,
                      color:
                        option.cost > 0
                          ? theme.errorText
                          : theme.pageTextSubdued,
                    }}
                  >
                    {t('{{amount}} de coût', { amount: money(option.cost) })}
                  </Text>
                </View>
              </View>
            ))}
            <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
              {proposal?.explanation ||
                t('Comble le manque de {{amount}} au moindre coût.', {
                  amount: money(shortfall),
                })}
            </Text>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                paddingTop: 8,
                borderTop: `1px solid ${theme.tableBorder}`,
              }}
            >
              <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                {t('Coût de cette solution')}
              </Text>
              <Text style={{ fontWeight: 600 }}>{money(chosenCost)}</Text>
            </View>
          </Section>
        )}

        {others.length > 0 && (
          <Section title={t('Autres options, avec leur coût')}>
            {others.map((option, index) => (
              <Line
                key={option.id}
                last={index === others.length - 1}
                title={label(option)}
                detail={
                  option.kind === 'partial'
                    ? t('Libère {{amount}} à temps · démarche à faire', {
                        amount: money(option.frees),
                      })
                    : t('Libère {{amount}} à temps', {
                        amount: money(option.frees),
                      })
                }
                value={money(option.cost)}
                valueDetail={t('de coût')}
                valueColor={option.cost > 0 ? theme.errorText : undefined}
              />
            ))}
          </Section>
        )}

        {shortfall > 0 && (
          <Text
            style={{
              fontSize: 12,
              color: theme.pageTextSubdued,
              padding: '0 4px',
            }}
          >
            {t(
              "Aucun nom de créancier ni numéro de compte n'est envoyé au moteur d'IA. Les montants viennent du calcul, pas de l'IA.",
            )}
          </Text>
        )}

        {chosen.length > 0 && (
          <Button
            variant="primary"
            onPress={accept}
            style={{ minHeight: 48, fontWeight: 600 }}
          >
            {t('Retenir la solution proposée')}
          </Button>
        )}
        <Button
          variant="bare"
          onPress={() => navigate('/quinzaine')}
          style={{ minHeight: 44 }}
        >
          <Trans>Ne rien changer</Trans>
        </Button>
      </View>
    </MtxPage>
  );
}
